'use client';

import React, { useEffect, useState } from 'react';
import { SearchBarPortal } from './SearchBarPortal';

import { usePathname, useRouter } from 'next/navigation';

import BuildingsMiniSearchBar from '@/features/buildings/BuildingsMiniSearchBar';
import BuildingsSearchBar from '@/features/buildings/BuildingsSearchBar';
import ListingsSearchBar from '@/features/listings/ListingsSearchBar';
import ListingsMiniSearchBar from '@/features/listings/ListingsMiniSearchBar';
import { SearchBarTabs } from '@/components/shared/HeaderTabbedSearchBar';
import { AISearchBar } from '@/features/ai-search/AISearchBar';
import { AiMiniSearchBar } from '@/features/ai-search/AiMiniSearchBar';
import { useAiSearchStatus } from '@/features/ai-search/hooks/useAiSearchStatus';
import { useAnalytics } from '@/hooks/useAnalytics';

type ActivePanel = 'price' | 'beds' | 'location' | null;

const ANIMATION_DURATION = 500;

export function SearchBar() {
  const pathname = usePathname();
  const isReviews = pathname === '/read-reviews';
  const [showTabs, setShowTabs] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'rent' | 'reviews'>(
    isReviews ? 'reviews' : 'rent',
  );
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  // Whether the expanded rent AI bar should grab focus (set when the mini bar's
  // text is tapped, not its home icon).
  const [focusSearch, setFocusSearch] = useState(false);
  const analytics = useAnalytics();
  const router = useRouter();
  const status = useAiSearchStatus();
  const defaultTab = isReviews ? 'reviews' : 'rent';
  // AI search replaces the legacy rent search on the home page (rent side only,
  // and rendered by default while the switch is still loading — no legacy flash).
  const aiRentEnabled = !isReviews && status !== 'disabled';

  useEffect(() => {
    setSelectedTab(defaultTab);
  }, [defaultTab]);

  // Reset to default tab when search bar is closed
  useEffect(() => {
    if (!showTabs) {
      setSelectedTab(defaultTab);
      setActivePanel(null);
      setFocusSearch(false);
    }
  }, [showTabs, defaultTab]);

  const handlePanelClick = (panel: ActivePanel) => {
    setShowTabs(true);
    // Delay setting activePanel to allow animation to complete
    // The animation has delay-500 (500ms), so we wait a bit longer
    setTimeout(() => {
      setActivePanel(panel);
      // 1ms more than the animation delay-500 (500ms)
    }, ANIMATION_DURATION + 1);
  };

  const collapsedContent = isReviews ? (
    <BuildingsMiniSearchBar
      onClick={() => setShowTabs(true)}
      onSearchClick={() => setShowTabs(true)}
      onLeftIconClick={() => setShowTabs(true)}
    />
  ) : aiRentEnabled ? (
    <AiMiniSearchBar
      onHomeClick={() => setShowTabs(true)}
      onTextClick={() => {
        setShowTabs(true);
        setFocusSearch(true);
      }}
      onSearchClick={() => router.push('/listings/status:verified')}
    />
  ) : (
    <ListingsMiniSearchBar
      onPriceClick={() => handlePanelClick('price')}
      onBedsClick={() => handlePanelClick('beds')}
      onLocationClick={() => handlePanelClick('location')}
      onLeftIconClick={() => setShowTabs(true)}
    />
  );

  const handleTabClick = (tab: 'rent' | 'reviews') => {
    if (tab === 'rent') {
      analytics.track('header_explore_listings');
      setSelectedTab('rent');
    } else {
      analytics.track('header_read_review');
      setSelectedTab('reviews');
    }
  };
  return (
    <>
      <SearchBarPortal
        collapsedContent={
          showTabs ? (
            <SearchBarTabs onTabClick={handleTabClick} selectedTab={selectedTab} />
          ) : (
            collapsedContent
          )
        }
      >
        {isReviews ? (
          <BuildingsSearchBar />
        ) : aiRentEnabled ? (
          <div className="w-[600px] max-w-full">
            <AISearchBar />
          </div>
        ) : (
          <ListingsSearchBar />
        )}
      </SearchBarPortal>
      {showTabs && (
        <div className="absolute top-header flex flex-col left-0 w-full h-[calc(100%-var(--spacing-header))] z-50 bg-black/20">
          <div className="flex items-center justify-center bg-secondary pb-8 animate-slide-down delay-[${ANIMATION_DURATION}ms]">
            {selectedTab === 'rent' ? (
              aiRentEnabled ? (
                <div className="w-[600px] max-w-full">
                  <AISearchBar autoFocus={focusSearch} />
                </div>
              ) : (
                <ListingsSearchBar initialActivePanel={activePanel} />
              )
            ) : (
              <BuildingsSearchBar autoFocus={selectedTab === 'reviews'} />
            )}
          </div>
          <div
            className="flex-1"
            onClick={() => {
              setShowTabs(false);
            }}
          />
        </div>
      )}
    </>
  );
}
